import type { CurvePoint, GradeData, PixelInto } from './grade';
import { blendChannel, BLEND_MODE_INDEX, type CubeLut } from './compositing';
import { compileGradeInto, fullGrade, gradeIsNeutral, hueOf, sanitizeGrade, smoothstep } from './grade';

/**
 * Correction nodes, Resolve style. A clip's grade is an ordered list of
 * nodes (ext.nodes); each one takes the output of the one before. A node
 * has its own correction (a GradeData: wheels, curves, mixer…), a key mix,
 * and optionally a qualifier (HSL key on the node's input colour) and power
 * windows (shapes in the clip's box, combined with add / subtract /
 * intersect… modes). A single plain node is stored as the older
 * `ext.grade`, and a node with one window keeps it in `window`, so existing
 * projects keep their documents.
 *
 * A node can also be a mixer group: `kind: 'parallel'` or `'layer'` with up
 * to MAX_GROUP_INPUTS input nodes that all take the group's input. A
 * parallel mixer averages their outputs (by weight); a layer mixer stacks
 * them, later inputs over earlier ones, each composited through its own key
 * with a blend mode. In the node strip a group is one box in the serial line.
 *
 * Rendering (preview and export alike) plans the list into stages:
 *  - consecutive global nodes (no qualifier, no window) fold into one baked
 *    3D LUT, mixes included — a per-pixel function, like a single grade;
 *  - a keyed node is its own stage: its correction baked into a LUT, its
 *    qualifier matte baked into a second LUT (RGB → matte), its windows a
 *    shape mask; output = mix(input, corrected, matte × windows × key mix);
 *  - every input of a mixer group is a stage of its own, marked with the
 *    group it belongs to.
 * The preview evaluates stages in one shader (LUTs in a 3D atlas, the matte
 * in the alpha channel); the export splits the picture, runs lut3d on both
 * branches, builds the window masks with geq (Bézier windows: rasterized
 * here and read as an image; perspective when they move) and joins them
 * with maskedmerge (mixers: mix / blend).
 */

export const MAX_NODES = 8;
/** Inputs of one parallel or layer mixer. */
export const MAX_GROUP_INPUTS = 4;
/** Every node of a clip, mixer inputs included (one render stage at most each). */
export const MAX_TOTAL_NODES = 16;
/** Nodes whose matte is blurred or denoised (the preview keeps one texture each). */
export const MAX_BLURRED_MATTES = 2;
export const MAX_POLYGON_POINTS = 16;
export const MAX_WINDOW_KEYS = 240;
/** Windows of one node, and of a whole clip. */
export const MAX_WINDOWS = 8;
export const MAX_CLIP_WINDOWS = 16;
/** Vertices of a Bézier window, and points of its flattened outline. */
export const MAX_BEZIER_VERTICES = 24;
export const MAX_FLAT_POINTS = 128;
/** Largest matte denoise (median) radius, in project pixels. */
export const MAX_MATTE_DENOISE = 3;

export interface QualifierRange {
  low: number;
  high: number;
  soft: number;
}

export interface Qualifier {
  /** Hue range: centre (0..1, red at 0), full width (0..1 of the circle; ≥ 1 = every hue) and softness. */
  hue?: { center: number; width: number; soft: number };
  /** HSL saturation range (0..1). */
  sat?: QualifierRange;
  /** Luma range (0..1). */
  lum?: QualifierRange;
  invert?: boolean;
  /** Clean black / white: matte levels below `cleanBlack` go to 0, above 1 − `cleanWhite` to 1. */
  cleanBlack?: number;
  cleanWhite?: number;
  /** Matte denoise: median filter radius (1..3 project pixels), applied before the blur. */
  denoise?: number;
  /** Matte blur: gaussian sigma in project pixels (spatial, applied before the window). */
  blur?: number;
}

export type WindowShape = 'ellipse' | 'rect' | 'linear' | 'polygon' | 'bezier';
export const WINDOW_SHAPES: readonly WindowShape[] = ['ellipse', 'rect', 'linear', 'polygon', 'bezier'];

/**
 * How a window combines with the windows before it in its node (After
 * Effects' mask modes): add = union (a + m − a·m), subtract = a·(1 − m),
 * intersect = a·m, lighten = max, darken = min, difference = a + m − 2·a·m.
 * The first window starts from nothing (add, lighten, difference) or from
 * the whole picture (subtract, intersect, darken).
 */
export type WindowMode = 'add' | 'subtract' | 'intersect' | 'lighten' | 'darken' | 'difference';
export const WINDOW_MODES: readonly WindowMode[] = ['add', 'subtract', 'intersect', 'lighten', 'darken', 'difference'];

/** A Bézier vertex in window units: anchor (x, y), then its incoming and outgoing handles relative to it (zero = corner). */
export type BezierVertex = [number, number, number, number, number, number];

/** Circle tangent length for a 4-segment cubic approximation of a quarter circle. */
export const KAPPA = 0.5522847498307934;

/**
 * Where a window sits: centre (x, y) in the clip's box (0..1, y down), size as
 * fractions of the box width and height, rotation in degrees (clockwise).
 * A linear window is a soft edge through the centre, across its height.
 */
export interface WindowPose {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
}

/** A window keyframe at clip-local time `t` (µs). */
export interface WindowKey extends WindowPose {
  t: number;
}

export interface GradeWindow extends WindowPose {
  shape: WindowShape;
  /** Softness (feather), in the window's own units (1 = half its size), centred on the edge. */
  soft: number;
  invert?: boolean;
  /** Grows (+) or shrinks (−) the shape, in window units (default 0). */
  expansion?: number;
  /** 0..1 (default 1). */
  opacity?: number;
  /** How it combines with the node's windows before it (default add). */
  mode?: WindowMode;
  /** Polygon points in window units (−1..1 across the window's width and height). */
  points?: CurvePoint[];
  /** Bézier windows: the closed path, in window units. */
  path?: BezierVertex[];
  /** Keyframes of the pose (linear in between; held before the first and after the last). */
  keys?: WindowKey[];
  /** The motion track its keys were made from. */
  track?: string;
}

export type MixerKind = 'parallel' | 'layer';

export interface GradeNode {
  id: string;
  label?: string;
  /** false = bypassed. */
  enabled?: boolean;
  /** Key mix: how much of the correction applies (0..1, default 1). Mixer groups: how much of the mixer's output applies. */
  mix?: number;
  grade?: GradeData;
  qualifier?: Qualifier;
  /** The node's window when it has exactly one in add mode (older documents). */
  window?: GradeWindow;
  /** Its windows otherwise, combined in order by their modes. */
  windows?: GradeWindow[];
  /** A mixer group: its `inputs` all take the group's input; parallel averages them, layer stacks them. */
  kind?: MixerKind;
  inputs?: GradeNode[];
  /** Parallel mixer inputs: relative weight (default 1). */
  weight?: number;
  /** Layer mixer inputs: blend mode over the layers below (BLEND_MODES id, default normal). */
  composite?: string;
}

const EPS = 1e-6;
const num = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : undefined;
const r4 = (v: number) => Math.round(v * 1e4) / 1e4;
const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** The Bézier path of a circle in window units (four smooth vertices). */
export const CIRCLE_PATH: BezierVertex[] = [
  [1, 0, 0, -KAPPA, 0, KAPPA],
  [0, 1, KAPPA, 0, -KAPPA, 0],
  [-1, 0, 0, KAPPA, 0, -KAPPA],
  [0, -1, -KAPPA, 0, KAPPA, 0],
].map((v) => v.map(r4) as BezierVertex);

export const DEFAULT_WINDOW: Record<WindowShape, Omit<GradeWindow, 'shape'>> = {
  ellipse: { x: 0.5, y: 0.5, w: 0.4, h: 0.5, rotation: 0, soft: 0.3 },
  rect: { x: 0.5, y: 0.5, w: 0.5, h: 0.5, rotation: 0, soft: 0.1 },
  linear: { x: 0.5, y: 0.5, w: 1, h: 0.5, rotation: 0, soft: 0.5 },
  polygon: {
    x: 0.5,
    y: 0.5,
    w: 0.4,
    h: 0.4,
    rotation: 0,
    soft: 0.1,
    points: [
      [0, -1],
      [1, -0.2],
      [0.6, 1],
      [-0.6, 1],
      [-1, -0.2],
    ],
  },
  bezier: { x: 0.5, y: 0.5, w: 0.4, h: 0.5, rotation: 0, soft: 0.15, path: CIRCLE_PATH },
};

export const DEFAULT_QUALIFIER: Qualifier = { hue: { center: 0.08, width: 0.1, soft: 0.05 }, sat: { low: 0.15, high: 1, soft: 0.1 } };

function sanitizeRange(raw: unknown): QualifierRange | undefined {
  const o = obj(raw);
  if (!o) return undefined;
  const low = num(o.low, 0, 1) ?? 0;
  const high = Math.max(low, num(o.high, 0, 1) ?? 1);
  return { low: r4(low), high: r4(high), soft: r4(num(o.soft, 0, 0.5) ?? 0.05) };
}

export function sanitizeQualifier(raw: unknown): Qualifier | undefined {
  const o = obj(raw);
  if (!o) return undefined;
  const out: Qualifier = {};
  const h = obj(o.hue);
  if (h) out.hue = { center: r4(((num(h.center, -10, 10) ?? 0) % 1 + 1) % 1), width: r4(num(h.width, 0, 1) ?? 0.1), soft: r4(num(h.soft, 0, 0.5) ?? 0.05) };
  const sat = sanitizeRange(o.sat);
  if (sat) out.sat = sat;
  const lum = sanitizeRange(o.lum);
  if (lum) out.lum = lum;
  if (!out.hue && !out.sat && !out.lum) return undefined;
  if (o.invert === true) out.invert = true;
  const cb = num(o.cleanBlack, 0, 0.49);
  if (cb) out.cleanBlack = r4(cb);
  const cw = num(o.cleanWhite, 0, 0.49);
  if (cw) out.cleanWhite = r4(cw);
  const dn = num(o.denoise, 0, MAX_MATTE_DENOISE);
  if (dn && Math.round(dn) >= 1) out.denoise = Math.round(dn);
  const blur = num(o.blur, 0, 50);
  if (blur && blur >= 0.1) out.blur = r4(blur);
  return out;
}

function sanitizePose(o: Record<string, unknown>, d: WindowPose): WindowPose {
  return {
    x: r4(num(o.x, -1, 2) ?? d.x),
    y: r4(num(o.y, -1, 2) ?? d.y),
    w: r4(num(o.w, 0.005, 4) ?? d.w),
    h: r4(num(o.h, 0.005, 4) ?? d.h),
    rotation: r4(num(o.rotation, -3600, 3600) ?? d.rotation),
  };
}

function sanitizePath(raw: unknown): BezierVertex[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out = raw.slice(0, MAX_BEZIER_VERTICES).flatMap((v) => {
    if (!Array.isArray(v) || v.length < 2) return [];
    const x = num(v[0], -1, 1);
    const y = num(v[1], -1, 1);
    if (x === undefined || y === undefined) return [];
    const t = [2, 3, 4, 5].map((k) => r4(num(v[k], -2, 2) ?? 0));
    return [[r4(x), r4(y), ...t] as BezierVertex];
  });
  return out.length >= 3 ? out : undefined;
}

export function sanitizeWindow(raw: unknown): GradeWindow | undefined {
  const o = obj(raw);
  if (!o) return undefined;
  const shape = (WINDOW_SHAPES as readonly unknown[]).includes(o.shape) ? (o.shape as WindowShape) : 'ellipse';
  const d = DEFAULT_WINDOW[shape];
  const out: GradeWindow = { shape, ...sanitizePose(o, d), soft: r4(num(o.soft, 0, 1) ?? d.soft) };
  if (o.invert === true) out.invert = true;
  const ex = num(o.expansion, -1, 1);
  if (ex && Math.abs(ex) >= 1e-4) out.expansion = r4(ex);
  const op = num(o.opacity, 0, 1);
  if (op !== undefined && op < 1 - 1e-4) out.opacity = r4(op);
  if (typeof o.mode === 'string' && o.mode !== 'add' && (WINDOW_MODES as readonly string[]).includes(o.mode)) out.mode = o.mode as WindowMode;
  if (shape === 'polygon') {
    const pts = (Array.isArray(o.points) ? o.points : d.points ?? []).slice(0, MAX_POLYGON_POINTS).flatMap((p) => {
      if (!Array.isArray(p)) return [];
      const x = num(p[0], -1, 1);
      const y = num(p[1], -1, 1);
      return x === undefined || y === undefined ? [] : [[r4(x), r4(y)] as CurvePoint];
    });
    out.points = pts.length >= 3 ? pts : d.points;
  }
  if (shape === 'bezier') out.path = sanitizePath(o.path) ?? CIRCLE_PATH;
  if (Array.isArray(o.keys)) {
    const keys = o.keys
      .slice(0, MAX_WINDOW_KEYS)
      .flatMap((k) => {
        const ko = obj(k);
        const t = num(ko?.t, 0, 1e13);
        return ko && t !== undefined ? [{ t: Math.round(t), ...sanitizePose(ko, out) }] : [];
      })
      .sort((a, b) => a.t - b.t)
      .filter((k, i, a) => i === 0 || k.t !== a[i - 1].t);
    if (keys.length) out.keys = keys;
  }
  if (typeof o.track === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(o.track)) out.track = o.track;
  return out;
}

const NODE_ID = /^[A-Za-z0-9_-]{1,24}$/;

/** The node's windows, in order (the single `window` of older documents included). */
export function nodeWindows(n: Pick<GradeNode, 'window' | 'windows'>): GradeWindow[] {
  return n.windows?.length ? n.windows : n.window ? [n.window] : [];
}

/** The node with a new window list (one window in add mode is stored as `window`, as before). */
export function withNodeWindows<N extends GradeNode>(n: N, windows: readonly GradeWindow[]): N {
  const { window: _w, windows: _ws, ...rest } = n;
  if (!windows.length) return rest as N;
  if (windows.length === 1 && (windows[0].mode ?? 'add') === 'add') return { ...rest, window: windows[0] } as N;
  return { ...rest, windows: [...windows] } as N;
}

export const nodeIsGroup = (n: GradeNode) => !!n.kind;

interface SanitizeBudget {
  seen: Set<string>;
  blurred: number;
  windows: number;
  total: number;
}

function sanitizeNode(o: Record<string, unknown>, b: SanitizeBudget, inGroup: boolean): GradeNode | null {
  if (b.total >= MAX_TOTAL_NODES) return null;
  let id = typeof o.id === 'string' && NODE_ID.test(o.id) ? o.id : '';
  if (!id || b.seen.has(id)) id = freshNodeId(b.seen);
  b.seen.add(id);
  const node: GradeNode = { id };
  if (typeof o.label === 'string' && o.label.trim()) node.label = o.label.trim().slice(0, 32);
  if (o.enabled === false) node.enabled = false;
  const mix = num(o.mix, 0, 1);
  if (mix !== undefined && mix < 1 - EPS) node.mix = r4(mix);
  if (!inGroup && (o.kind === 'parallel' || o.kind === 'layer')) {
    // A mixer group: its inputs are plain or keyed nodes (no nested groups).
    node.kind = o.kind;
    const inputs: GradeNode[] = [];
    for (const raw of Array.isArray(o.inputs) ? o.inputs.slice(0, MAX_GROUP_INPUTS) : []) {
      const io = obj(raw);
      if (!io) continue;
      const n = sanitizeNode(io, b, true);
      if (n) inputs.push(n);
    }
    if (!inputs.length) {
      if (b.total >= MAX_TOTAL_NODES) return null;
      const n: GradeNode = { id: freshNodeId(b.seen) };
      b.seen.add(n.id);
      b.total++;
      inputs.push(n);
    }
    node.inputs = inputs;
    return node;
  }
  b.total++;
  const grade = sanitizeGrade(o.grade);
  if (grade) node.grade = grade;
  const q = sanitizeQualifier(o.qualifier);
  if (q) {
    if ((q.blur || q.denoise) && ++b.blurred > MAX_BLURRED_MATTES) {
      delete q.blur;
      delete q.denoise;
    }
    node.qualifier = q;
  }
  const rawWindows = Array.isArray(o.windows) && o.windows.length ? o.windows : o.window ? [o.window] : [];
  const windows: GradeWindow[] = [];
  for (const rw of rawWindows.slice(0, MAX_WINDOWS)) {
    if (b.windows >= MAX_CLIP_WINDOWS) break;
    const w = sanitizeWindow(rw);
    if (!w) continue;
    windows.push(w);
    b.windows++;
  }
  if (inGroup) {
    const weight = num(o.weight, 0, 10);
    if (weight !== undefined && Math.abs(weight - 1) > 1e-4) node.weight = r4(weight);
    if (typeof o.composite === 'string' && o.composite !== 'normal' && o.composite in BLEND_MODE_INDEX) node.composite = o.composite;
  }
  return windows.length ? withNodeWindows(node, windows) : node;
}

export function sanitizeNodes(raw: unknown): GradeNode[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const budget: SanitizeBudget = { seen: new Set(), blurred: 0, windows: 0, total: 0 };
  const out: GradeNode[] = [];
  for (const item of raw.slice(0, MAX_NODES)) {
    const o = obj(item);
    if (!o) continue;
    const node = sanitizeNode(o, budget, false);
    if (node) out.push(node);
  }
  return out.length ? out : undefined;
}

function freshNodeId(taken: Set<string>): string {
  for (let i = 1; ; i++) if (!taken.has(`n${i}`)) return `n${i}`;
}

/** Every node of a list, mixer inputs included (groups first, then their inputs). */
export function allNodes(nodes: readonly GradeNode[]): GradeNode[] {
  return nodes.flatMap((n) => (n.inputs ? [n, ...n.inputs] : [n]));
}

/** A new node id not used in `nodes` (mixer inputs included). */
export const newNodeId = (nodes: readonly GradeNode[]) => freshNodeId(new Set(allNodes(nodes).map((n) => n.id)));

/** Nodes that render (mixer inputs, not their groups), counted against MAX_TOTAL_NODES. */
export const correctionNodeCount = (nodes: readonly GradeNode[]) => nodes.reduce((k, n) => k + (n.inputs ? n.inputs.length : 1), 0);

/** A node that is just a correction (what the single `ext.grade` stores). */
export const nodeIsPlain = (n: GradeNode) => n.enabled !== false && n.mix === undefined && !n.label && !n.qualifier && !n.window && !n.windows && !n.kind;

/** The node list of a clip's ext (the legacy single grade is one node). */
export function gradeNodes(ext: { grade?: GradeData; nodes?: GradeNode[] }): GradeNode[] {
  if (ext.nodes?.length) return ext.nodes;
  return ext.grade ? [{ id: 'n1', grade: ext.grade }] : [];
}

/** The ext fields storing a node list (one plain node collapses to `grade`). */
export function nodesToExt(nodes: readonly GradeNode[]): { grade?: GradeData; nodes?: GradeNode[] } {
  if (!nodes.length) return { grade: undefined, nodes: undefined };
  if (nodes.length === 1 && nodeIsPlain(nodes[0])) return { grade: nodes[0].grade, nodes: undefined };
  return { grade: undefined, nodes: [...nodes] };
}

export const nodeIsKeyed = (n: GradeNode) => !!(n.qualifier || n.window || n.windows?.length);

/**
 * The node whose grade a keyframe property animates: plain names belong to
 * the first correction node (the first input of a leading mixer group),
 * `<property>@<id>` to the node with that id. Null when there is none.
 */
export function nodeOfProperty(nodes: readonly GradeNode[], nodeId: string | undefined): GradeNode | null {
  if (nodeId === undefined) {
    const first = nodes[0];
    return first ? (first.inputs ? first.inputs[0] ?? null : first) : null;
  }
  return allNodes(nodes).find((n) => n.id === nodeId && !n.kind) ?? null;
}

/** The node list with node `id` (anywhere, mixer inputs included) replaced by `fn` of it. */
export function mapNode(nodes: readonly GradeNode[], id: string, fn: (n: GradeNode) => GradeNode): GradeNode[] {
  return nodes.map((n) => (n.id === id ? fn(n) : n.inputs ? { ...n, inputs: n.inputs.map((x) => (x.id === id ? fn(x) : x)) } : n));
}

// ---------------------------------------------------------------------------
// Qualifier matte
// ---------------------------------------------------------------------------

/** HSL saturation of a colour (0 for greys). */
export function hslSaturation(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const c = max - min;
  const d = 1 - Math.abs(max + min - 1);
  return c < 1e-9 || d < 1e-9 ? 0 : Math.min(1, c / d);
}

function band(v: number, r: QualifierRange): number {
  const lo = r.low <= EPS ? 1 : r.soft > EPS ? smoothstep(r.low - r.soft, r.low, v) : v >= r.low ? 1 : 0;
  const hi = r.high >= 1 - EPS ? 1 : r.soft > EPS ? 1 - smoothstep(r.high, r.high + r.soft, v) : v <= r.high ? 1 : 0;
  return lo * hi;
}

/** The qualifier's matte (0..1) for a colour; clean levels and invert included. */
export function qualifierMatte(q: Qualifier, r: number, g: number, b: number): number {
  r = Math.min(1, Math.max(0, r));
  g = Math.min(1, Math.max(0, g));
  b = Math.min(1, Math.max(0, b));
  let m = 1;
  if (q.hue && q.hue.width < 1) {
    const h = hueOf(r, g, b);
    let d = Math.abs(h - q.hue.center);
    d = Math.min(d, 1 - d);
    const half = q.hue.width / 2;
    const mh = q.hue.soft > EPS ? 1 - smoothstep(half, half + q.hue.soft, d) : d <= half ? 1 : 0;
    // Greys have no hue: fade the hue key out below a little chroma.
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    m *= mh * smoothstep(0, 0.04, chroma);
  }
  if (q.sat) m *= band(hslSaturation(r, g, b), q.sat);
  if (q.lum) m *= band(0.299 * r + 0.587 * g + 0.114 * b, q.lum);
  const cb = q.cleanBlack ?? 0;
  const cw = q.cleanWhite ?? 0;
  if (cb || cw) m = Math.min(1, Math.max(0, (m - cb) / Math.max(EPS, 1 - cb - cw)));
  return q.invert ? 1 - m : m;
}

/** A qualifier picked from a colour: its hue (when it has one), saturation and luma, with room around them. */
export function qualifierFromColor(r: number, g: number, b: number, prev?: Qualifier): Qualifier {
  const s = hslSaturation(r, g, b);
  const y = 0.299 * r + 0.587 * g + 0.114 * b;
  const range = (v: number, half: number): QualifierRange => ({ low: r4(Math.max(0, v - half)), high: r4(Math.min(1, v + half)), soft: 0.08 });
  const q: Qualifier = { sat: range(s, 0.3), lum: range(y, 0.3) };
  if (Math.max(r, g, b) - Math.min(r, g, b) > 0.05) q.hue = { center: r4(hueOf(r, g, b)), width: 0.08, soft: 0.06 };
  if (prev?.invert) q.invert = true;
  if (prev?.denoise) q.denoise = prev.denoise;
  if (prev?.blur) q.blur = prev.blur;
  if (prev?.cleanBlack) q.cleanBlack = prev.cleanBlack;
  if (prev?.cleanWhite) q.cleanWhite = prev.cleanWhite;
  return q;
}

/** Median of the (2r+1)² neighbourhood of every sample of a plane (edges clamped, like ffmpeg `median`). */
export function medianPlane(src: ArrayLike<number>, w: number, h: number, r: number): Float32Array {
  const out = new Float32Array(w * h);
  const win: number[] = [];
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      win.length = 0;
      for (let dy = -r; dy <= r; dy++) {
        const yy = Math.min(h - 1, Math.max(0, y + dy));
        for (let dx = -r; dx <= r; dx++) win.push(src[yy * w + Math.min(w - 1, Math.max(0, x + dx))]);
      }
      win.sort((a, b) => a - b);
      out[y * w + x] = win[win.length >> 1];
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Power windows
// ---------------------------------------------------------------------------

/** The window's pose at clip-local time `tLocal` (its keys interpolated linearly). */
export function windowPoseAt(w: GradeWindow, tLocal: number): WindowPose {
  const k = w.keys;
  if (!k?.length) return { x: w.x, y: w.y, w: w.w, h: w.h, rotation: w.rotation };
  const pose = (p: WindowPose): WindowPose => ({ x: p.x, y: p.y, w: p.w, h: p.h, rotation: p.rotation });
  if (tLocal <= k[0].t) return pose(k[0]);
  if (tLocal >= k[k.length - 1].t) return pose(k[k.length - 1]);
  let i = 0;
  while (i < k.length - 2 && tLocal > k[i + 1].t) i++;
  const a = k[i];
  const b = k[i + 1];
  const f = (tLocal - a.t) / Math.max(1, b.t - a.t);
  const l = (u: number, v: number) => u + (v - u) * f;
  return { x: l(a.x, b.x), y: l(a.y, b.y), w: l(a.w, b.w), h: l(a.h, b.h), rotation: l(a.rotation, b.rotation) };
}

export const windowAnimated = (w: GradeWindow | undefined) =>
  !!w?.keys && w.keys.length > 1 && w.keys.some((k) => ['x', 'y', 'w', 'h', 'rotation'].some((p) => Math.abs(k[p as 'x'] - w.keys![0][p as 'x']) > 1e-6));

/** The window's pose with every key moved by the same change (for dragging an animated window). */
export function withWindowPose(w: GradeWindow, tLocal: number, next: WindowPose): GradeWindow {
  if (!w.keys?.length) return { ...w, ...next };
  const cur = windowPoseAt(w, tLocal);
  const at = w.keys.findIndex((k) => Math.abs(k.t - tLocal) < 1000);
  if (at >= 0) return { ...w, ...next, keys: w.keys.map((k, i) => (i === at ? { t: k.t, ...next } : k)) };
  // Between keys: offset every key (moves the whole path), like Resolve's tracker offset.
  const d = { x: next.x - cur.x, y: next.y - cur.y, w: next.w / Math.max(1e-6, cur.w), h: next.h / Math.max(1e-6, cur.h), rotation: next.rotation - cur.rotation };
  return {
    ...w,
    ...next,
    keys: w.keys.map((k) => ({ t: k.t, x: r4(k.x + d.x), y: r4(k.y + d.y), w: r4(k.w * d.w), h: r4(k.h * d.h), rotation: r4(k.rotation + d.rotation) })),
  };
}

/** Polygon: signed distance (positive inside) in window units. */
export function polygonSigned(points: readonly CurvePoint[], u: number, v: number): number {
  let inside = false;
  let best = Infinity;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i];
    const [xj, yj] = points[j];
    if (yi > v !== yj > v && u < ((xj - xi) * (v - yi)) / (yj - yi) + xi) inside = !inside;
    const dx = xj - xi;
    const dy = yj - yi;
    const l2 = dx * dx + dy * dy;
    const t = l2 > 0 ? Math.min(1, Math.max(0, ((u - xi) * dx + (v - yi) * dy) / l2)) : 0;
    const ex = u - xi - t * dx;
    const ey = v - yi - t * dy;
    best = Math.min(best, ex * ex + ey * ey);
  }
  const d = Math.sqrt(best);
  return inside ? d : -d;
}

/** Point on a cubic Bézier at `t`. */
function cubic(p0: number, c1: number, c2: number, p3: number, t: number): number {
  const u = 1 - t;
  return p0 * u * u * u + 3 * c1 * u * u * t + 3 * c2 * u * t * t + p3 * t * t * t;
}

const flatCache = new Map<string, CurvePoint[]>();

/**
 * The outline of a closed Bézier path as a polygon (window units): each
 * segment cut into chords by Wang's bound for a tolerance, coarser until it
 * fits in MAX_FLAT_POINTS. The preview and the export both fill this polygon.
 */
export function flattenBezier(path: readonly BezierVertex[]): CurvePoint[] {
  const key = JSON.stringify(path);
  const hit = flatCache.get(key);
  if (hit) return hit;
  const n = path.length;
  let out: CurvePoint[] = [];
  for (let tol = 0.002; ; tol *= 2) {
    out = [];
    for (let i = 0; i < n; i++) {
      const a = path[i];
      const b = path[(i + 1) % n];
      const P = [a[0], a[1]];
      const C1 = [a[0] + a[4], a[1] + a[5]];
      const C2 = [b[0] + b[2], b[1] + b[3]];
      const Q = [b[0], b[1]];
      out.push([r4(P[0]), r4(P[1])]);
      const straight = Math.abs(a[4]) + Math.abs(a[5]) + Math.abs(b[2]) + Math.abs(b[3]) < 1e-9;
      if (straight) continue;
      const d1 = Math.hypot(P[0] - 2 * C1[0] + C2[0], P[1] - 2 * C1[1] + C2[1]);
      const d2 = Math.hypot(C1[0] - 2 * C2[0] + Q[0], C1[1] - 2 * C2[1] + Q[1]);
      const steps = Math.min(16, Math.max(1, Math.ceil(Math.sqrt((0.75 * Math.max(d1, d2)) / tol))));
      for (let k = 1; k < steps; k++) {
        const t = k / steps;
        out.push([r4(cubic(P[0], C1[0], C2[0], Q[0], t)), r4(cubic(P[1], C1[1], C2[1], Q[1], t))]);
      }
    }
    if (out.length <= MAX_FLAT_POINTS || tol > 1) break;
  }
  out = out.slice(0, MAX_FLAT_POINTS);
  if (flatCache.size > 256) flatCache.clear();
  flatCache.set(key, out);
  return out;
}

/** The polygon a window is filled as (polygons and Bézier paths; empty for the analytic shapes). */
export function windowOutline(w: Pick<GradeWindow, 'shape' | 'points' | 'path'>): CurvePoint[] {
  if (w.shape === 'polygon') return w.points ?? [];
  if (w.shape === 'bezier') return flattenBezier(w.path ?? CIRCLE_PATH);
  return [];
}

/** How far the shape reaches from its centre, in window units (linear windows: unbounded). */
export function windowExtent(w: Pick<GradeWindow, 'shape' | 'points' | 'path'>): number {
  if (w.shape === 'linear') return Infinity;
  if (w.shape !== 'bezier') return 1;
  return Math.max(1e-3, ...windowOutline(w).map(([u, v]) => Math.max(Math.abs(u), Math.abs(v))));
}

/** A window's shape as a Bézier path (ellipse: four smooth vertices; rectangle and polygon: corners); null for linear windows. */
export function windowToBezier(w: Pick<GradeWindow, 'shape' | 'points' | 'path'>): BezierVertex[] | null {
  switch (w.shape) {
    case 'bezier':
      return w.path ?? CIRCLE_PATH;
    case 'ellipse':
      return CIRCLE_PATH;
    case 'rect':
      return [
        [-1, -1, 0, 0, 0, 0],
        [1, -1, 0, 0, 0, 0],
        [1, 1, 0, 0, 0, 0],
        [-1, 1, 0, 0, 0, 0],
      ];
    case 'polygon':
      return (w.points ?? []).map(([x, y]) => [x, y, 0, 0, 0, 0]);
    default:
      return null;
  }
}

/** Splits segment `i` of a path (vertex i → i+1) at `t`, keeping its shape (de Casteljau). */
export function splitBezier(path: readonly BezierVertex[], i: number, t = 0.5): BezierVertex[] {
  const n = path.length;
  if (n >= MAX_BEZIER_VERTICES) return [...path];
  const a = path[i];
  const b = path[(i + 1) % n];
  const lerp = (p: number[], q: number[]) => [p[0] + (q[0] - p[0]) * t, p[1] + (q[1] - p[1]) * t];
  const P0 = [a[0], a[1]];
  const P1 = [a[0] + a[4], a[1] + a[5]];
  const P2 = [b[0] + b[2], b[1] + b[3]];
  const P3 = [b[0], b[1]];
  const A = lerp(P0, P1);
  const B = lerp(P1, P2);
  const C = lerp(P2, P3);
  const D = lerp(A, B);
  const E = lerp(B, C);
  const M = lerp(D, E);
  const out = path.map((v) => [...v] as BezierVertex);
  out[i] = [a[0], a[1], a[2], a[3], A[0] - P0[0], A[1] - P0[1]].map(r4) as BezierVertex;
  const j = (i + 1) % n;
  out[j] = [b[0], b[1], C[0] - P3[0], C[1] - P3[1], b[4], b[5]].map(r4) as BezierVertex;
  out.splice(i + 1, 0, [M[0], M[1], D[0] - M[0], D[1] - M[1], E[0] - M[0], E[1] - M[1]].map(r4) as BezierVertex);
  return out;
}

/** Signed distance (positive inside) of window units (u, v) to the shape's edge. */
export function windowSigned(w: Pick<GradeWindow, 'shape' | 'points' | 'path'>, u: number, v: number): number {
  switch (w.shape) {
    case 'ellipse':
      return 1 - Math.hypot(u, v);
    case 'rect':
      return Math.min(1 - Math.abs(u), 1 - Math.abs(v));
    case 'linear':
      return -v;
    case 'polygon':
    case 'bezier':
      return polygonSigned(windowOutline(w), u, v);
  }
}

/** Mask value from a signed distance: a smoothstep across ±soft (hard at 0). */
export function windowEdge(s: number, soft: number): number {
  return soft > EPS ? smoothstep(-soft, soft, s) : s >= 0 ? 1 : 0;
}

/** Window units of a point (px, py) in a box of bw × bh pixels. */
export function windowUnits(p: WindowPose, px: number, py: number, bw: number, bh: number): [number, number] {
  const a = Math.max(1e-6, (p.w * bw) / 2);
  const b = Math.max(1e-6, (p.h * bh) / 2);
  const rad = (p.rotation * Math.PI) / 180;
  const cs = Math.cos(rad);
  const sn = Math.sin(rad);
  const dx = px - p.x * bw;
  const dy = py - p.y * bh;
  return [(dx * cs + dy * sn) / a, (-dx * sn + dy * cs) / b];
}

/** The window's coverage (0..1: expansion, softness, invert and opacity included) at pixel (px, py) — pixel centres are at +0.5. */
export function windowMask(w: GradeWindow, pose: WindowPose, px: number, py: number, bw: number, bh: number): number {
  const [u, v] = windowUnits(pose, px, py, bw, bh);
  const m = windowEdge(windowSigned(w, u, v) + (w.expansion ?? 0), w.soft);
  return (w.invert ? 1 - m : m) * (w.opacity ?? 1);
}

/** Combines the coverage `a` of the windows so far with a window's `m`. */
export function combineWindow(mode: WindowMode | undefined, a: number, m: number): number {
  switch (mode) {
    case 'subtract':
      return a * (1 - m);
    case 'intersect':
      return a * m;
    case 'lighten':
      return Math.max(a, m);
    case 'darken':
      return Math.min(a, m);
    case 'difference':
      return a + m - 2 * a * m;
    default:
      return a + m - a * m;
  }
}

/** Coverage before the first window: nothing for additive modes, everything for subtractive ones. */
export const windowsStart = (first: GradeWindow | undefined) => (first?.mode === 'subtract' || first?.mode === 'intersect' || first?.mode === 'darken' ? 1 : 0);

/** The combined mask of a node's windows at a pixel (1 without windows). */
export function windowsMask(windows: readonly GradeWindow[], tLocal: number, px: number, py: number, bw: number, bh: number): number {
  if (!windows.length) return 1;
  let a = windowsStart(windows[0]);
  for (const w of windows) a = combineWindow(w.mode, a, windowMask(w, windowPoseAt(w, tLocal), px, py, bw, bh));
  return a;
}

/** Whether a node's windows need the general mask path (more than one, or any setting older renderers never had). */
export function windowsAdvanced(windows: readonly GradeWindow[]): boolean {
  return windows.length > 1 || windows.some((w) => w.shape === 'bezier' || !!w.expansion || w.opacity !== undefined || (w.mode ?? 'add') !== 'add');
}

/**
 * Window keys that follow a tracked object: the window keeps its offset from
 * the object at `atLocal`. Samples are the object's centre in box coordinates
 * at clip-local times; with `rot` (degrees, clockwise) and `scale` (tracks
 * with rotation and scale) the window also turns and grows with the object,
 * its offset turning and scaling around it (`aspect` = box width / height in
 * pixels). Keys are thinned (straight runs dropped) to at most MAX_WINDOW_KEYS.
 */
export function windowKeysFromTrack(
  w: GradeWindow,
  atLocal: number,
  samples: { tLocal: number; x: number; y: number; rot?: number; scale?: number }[],
  tolerance = 0.002,
  aspect = 1,
): WindowKey[] {
  const pts = samples.filter((s) => Number.isFinite(s.x) && Number.isFinite(s.y)).sort((a, b) => a.tLocal - b.tLocal);
  if (!pts.length) return [];
  const base = windowPoseAt(w, atLocal);
  let ref = pts[0];
  for (const s of pts) if (Math.abs(s.tLocal - atLocal) < Math.abs(ref.tLocal - atLocal)) ref = s;
  const dx = base.x - ref.x;
  const dy = base.y - ref.y;
  const keys = pts.map((s) => {
    const t = Math.max(0, Math.round(s.tLocal));
    const dr = (s.rot ?? 0) - (ref.rot ?? 0);
    const ds = (s.scale ?? 1) / Math.max(1e-6, ref.scale ?? 1);
    if (Math.abs(dr) < 1e-9 && Math.abs(ds - 1) < 1e-9) return { t, x: r4(s.x + dx), y: r4(s.y + dy), w: base.w, h: base.h, rotation: base.rotation };
    // The offset turns and scales with the object (in box pixels, so the turn is not skewed).
    const a = (dr * Math.PI) / 180;
    const ox = dx * aspect;
    const rx = (Math.cos(a) * ox - Math.sin(a) * dy) * ds;
    const ry = (Math.sin(a) * ox + Math.cos(a) * dy) * ds;
    return { t, x: r4(s.x + rx / aspect), y: r4(s.y + ry), w: r4(base.w * ds), h: r4(base.h * ds), rotation: r4(base.rotation + dr) };
  });
  // Ramer–Douglas–Peucker on the pose over time (position, plus size and rotation when they change).
  const keep = new Array(keys.length).fill(false);
  keep[0] = keep[keys.length - 1] = true;
  const simplify = (i: number, j: number, tol: number) => {
    if (j <= i + 1) return;
    let worst = -1;
    let at = -1;
    for (let k = i + 1; k < j; k++) {
      const f = (keys[k].t - keys[i].t) / Math.max(1, keys[j].t - keys[i].t);
      const ex = keys[i].x + (keys[j].x - keys[i].x) * f - keys[k].x;
      const ey = keys[i].y + (keys[j].y - keys[i].y) * f - keys[k].y;
      const ew = keys[i].w + (keys[j].w - keys[i].w) * f - keys[k].w;
      const eh = keys[i].h + (keys[j].h - keys[i].h) * f - keys[k].h;
      const er = keys[i].rotation + (keys[j].rotation - keys[i].rotation) * f - keys[k].rotation;
      const e = Math.max(Math.hypot(ex, ey), Math.abs(ew) / 2, Math.abs(eh) / 2, Math.abs(er) / 100);
      if (e > worst) {
        worst = e;
        at = k;
      }
    }
    if (worst > tol) {
      keep[at] = true;
      simplify(i, at, tol);
      simplify(at, j, tol);
    }
  };
  let tol = tolerance;
  let out = keys;
  for (let pass = 0; pass < 8; pass++) {
    keep.fill(false);
    keep[0] = keep[keys.length - 1] = true;
    simplify(0, keys.length - 1, tol);
    out = keys.filter((_, i) => keep[i]);
    if (out.length <= MAX_WINDOW_KEYS) break;
    tol *= 2;
  }
  return out.filter((k, i, a) => i === 0 || k.t !== a[i - 1].t);
}

// ---------------------------------------------------------------------------
// Planning: nodes → render stages
// ---------------------------------------------------------------------------

/** The mixer group a stage is an input of. */
export interface StageGroup {
  kind: MixerKind;
  /** First / last input of its group (the stages of a group are consecutive). */
  first: boolean;
  last: boolean;
  /** Parallel: the input's weight. */
  weight: number;
  /** Layer: blend mode over the layers below (BLEND_MODES id). */
  composite: string;
  /** The group's own mix (how much of the mixer output applies). */
  mix: number;
}

export interface GradeStage {
  /** Indices (in the node list) of the nodes in this stage (a mixer input: its group's). */
  nodes: number[];
  /** Mixer inputs: the input's index in its group. */
  input?: number;
  /** Colour of the stage (a keyed stage: its node's correction alone, without the key mix). */
  fn: PixelInto;
  /** Stable key of `fn` (for caching baked LUTs). */
  key: string;
  /** Keyed stages: the node's qualifier / windows / key mix. */
  keyed: boolean;
  qualifier?: Qualifier;
  windows: GradeWindow[];
  mix: number;
  group?: StageGroup;
}

const identityInto: PixelInto = (r, g, b, out, o) => {
  out[o] = r;
  out[o + 1] = g;
  out[o + 2] = b;
};

/**
 * Plans enabled nodes into stages. Bypassed nodes, nodes that change nothing
 * and nodes with a zero mix are skipped; with nothing left the list is empty.
 * `keepNeutral` keeps neutral nodes, so an animated grade has the same stages
 * at every moment (both renderers plan animated grades that way). Inputs of
 * a mixer are always kept (a neutral one still counts in the mix); bypassed
 * inputs are left out of it.
 */
export function planStages(nodes: readonly GradeNode[], o: { keepNeutral?: boolean } = {}): GradeStage[] {
  const stages: GradeStage[] = [];
  let run: { idx: number; node: GradeNode }[] = [];
  const flush = () => {
    if (!run.length) return;
    const parts = run.map(({ node }) => ({ f: compileGradeInto(fullGrade(node.grade)), mix: node.mix ?? 1 }));
    let fn: PixelInto;
    if (parts.length === 1 && parts[0].mix === 1) fn = parts[0].f;
    else {
      const tmp = [0, 0, 0];
      fn = (r, g, b, out, o) => {
        let c0 = r;
        let c1 = g;
        let c2 = b;
        for (const p of parts) {
          p.f(c0, c1, c2, tmp, 0);
          c0 += (tmp[0] - c0) * p.mix;
          c1 += (tmp[1] - c1) * p.mix;
          c2 += (tmp[2] - c2) * p.mix;
        }
        out[o] = c0;
        out[o + 1] = c1;
        out[o + 2] = c2;
      };
    }
    stages.push({ nodes: run.map((x) => x.idx), fn, key: JSON.stringify(run.map(({ node }) => [gradeOnlyKey(node.grade), node.mix ?? 1])), keyed: false, windows: [], mix: 1 });
    run = [];
  };
  nodes.forEach((node, idx) => {
    if (node.enabled === false || (node.mix ?? 1) <= EPS) return;
    if (node.kind) {
      flush();
      const inputs = (node.inputs ?? []).map((n, j) => ({ n, j })).filter(({ n }) => n.enabled !== false);
      inputs.forEach(({ n, j }, k) => {
        stages.push({
          nodes: [idx],
          input: j,
          fn: compileGradeInto(fullGrade(n.grade)),
          key: gradeOnlyKey(n.grade),
          keyed: nodeIsKeyed(n),
          qualifier: n.qualifier,
          windows: nodeWindows(n),
          mix: n.mix ?? 1,
          group: { kind: node.kind!, first: k === 0, last: k === inputs.length - 1, weight: n.weight ?? 1, composite: n.composite ?? 'normal', mix: node.mix ?? 1 },
        });
      });
      return;
    }
    const neutral = !o.keepNeutral && gradeIsNeutral(fullGrade(node.grade));
    if (nodeIsKeyed(node)) {
      flush();
      if (neutral) return;
      stages.push({
        nodes: [idx],
        fn: compileGradeInto(fullGrade(node.grade)),
        key: gradeOnlyKey(node.grade),
        keyed: true,
        qualifier: node.qualifier,
        windows: nodeWindows(node),
        mix: node.mix ?? 1,
      });
    } else if (!neutral) run.push({ idx, node });
  });
  flush();
  return stages;
}

/** Key of a node's per-pixel correction (pivot excluded: it acts on the basic contrast). */
function gradeOnlyKey(g: GradeData | undefined): string {
  if (!g) return '{}';
  const { pivot: _pivot, ...rest } = g;
  return JSON.stringify(rest);
}

/** RGB .cube data of a baked stage (`matte`: the alpha × `scale` in every channel instead). */
export function stageCube(rgba: Float32Array, size: number, matte = false, scale = 1): CubeLut {
  const n = size * size * size;
  const data = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    for (let k = 0; k < 3; k++) data[i * 3 + k] = matte ? rgba[i * 4 + 3] * scale : rgba[i * 4 + k];
  }
  return { size, data, domainMin: [0, 0, 0], domainMax: [1, 1, 1] };
}

/** Key of a stage's qualifier matte LUT ('' = none). */
export const matteKey = (q: Qualifier | undefined) => (q ? JSON.stringify({ ...q, blur: undefined, denoise: undefined }) : '');

/** Whether a stage's matte is processed spatially (denoise or blur: a texture of its own in the preview). */
export const matteSpatial = (s: Pick<GradeStage, 'qualifier'>) => !!(s.qualifier?.blur || s.qualifier?.denoise);

/**
 * A stage baked into RGBA (red fastest, then green, then blue): RGB = the
 * stage's colour, A = the qualifier matte of the input (1 without one). The
 * preview uploads it as is; the export splits it into two .cube files.
 */
export function bakeStage(stage: Pick<GradeStage, 'fn' | 'qualifier'>, size: number): Float32Array {
  const data = new Float32Array(size * size * size * 4);
  const f = stage.fn ?? identityInto;
  const q = stage.qualifier;
  const tmp = [0, 0, 0];
  let i = 0;
  for (let b = 0; b < size; b++) {
    for (let g = 0; g < size; g++) {
      for (let r = 0; r < size; r++, i += 4) {
        const rr = r / (size - 1);
        const gg = g / (size - 1);
        const bb = b / (size - 1);
        f(rr, gg, bb, tmp, 0);
        data[i] = Math.min(1, Math.max(0, tmp[0]));
        data[i + 1] = Math.min(1, Math.max(0, tmp[1]));
        data[i + 2] = Math.min(1, Math.max(0, tmp[2]));
        data[i + 3] = q ? qualifierMatte(q, rr, gg, bb) : 1;
      }
    }
  }
  return data;
}

type RGB = [number, number, number];
const mixRgb = (a: readonly number[], b: readonly number[], m: number): RGB => [a[0] + (b[0] - a[0]) * m, a[1] + (b[1] - a[1]) * m, a[2] + (b[2] - a[2]) * m];

/** A mixer's output from its inputs' corrected colours `L`, mattes `m` and the group input `g` (shared by evalNodes and stagesPixel). */
function mixGroup(kind: MixerKind, g: RGB, parts: { L: RGB; m: number; weight: number; composite: string }[], mix: number): RGB {
  let out: RGB;
  if (kind === 'parallel') {
    const total = parts.reduce((s, p) => s + p.weight, 0);
    const w = (p: { weight: number }) => (total > EPS ? p.weight / total : 1 / parts.length);
    out = [0, 0, 0];
    for (const p of parts) {
      const o = mixRgb(g, p.L, p.m);
      for (let k = 0; k < 3; k++) out[k] += o[k] * w(p);
    }
  } else {
    out = mixRgb(g, parts[0].L, parts[0].m);
    for (const p of parts.slice(1)) {
      const top = p.composite === 'normal' ? p.L : (out.map((b, k) => blendChannel(p.composite, b, p.L[k])) as RGB);
      out = mixRgb(out, top, p.m);
    }
  }
  return mix >= 1 - EPS ? out : mixRgb(g, out, mix);
}

/**
 * Reference evaluation of the node list for one pixel (exact maths, no LUTs,
 * no matte blur or denoise): the colour at (px, py) in a bw × bh box at
 * clip-local time `tLocal`. Used by tests and by the colour picker (a node's
 * input colour: the nodes before `upTo`).
 */
export function evalNodes(nodes: readonly GradeNode[], rgb: [number, number, number], px: number, py: number, bw: number, bh: number, tLocal: number, upTo = nodes.length): [number, number, number] {
  let c: RGB = [...rgb];
  const tmp = [0, 0, 0];
  /** A node's corrected colour (clamped) and matte for input `inp`. */
  const apply = (node: GradeNode, inp: RGB): { L: RGB; m: number } => {
    let m = node.mix ?? 1;
    if (node.qualifier) m *= qualifierMatte(node.qualifier, inp[0], inp[1], inp[2]);
    const ws = nodeWindows(node);
    if (ws.length) m *= windowsMask(ws, tLocal, px, py, bw, bh);
    compileGradeInto(fullGrade(node.grade))(inp[0], inp[1], inp[2], tmp, 0);
    return { L: [0, 1, 2].map((i) => Math.min(1, Math.max(0, tmp[i]))) as RGB, m };
  };
  nodes.slice(0, upTo).forEach((node) => {
    if (node.enabled === false) return;
    if (node.kind) {
      if ((node.mix ?? 1) <= EPS) return;
      const inputs = (node.inputs ?? []).filter((n) => n.enabled !== false);
      if (!inputs.length) return;
      const g: RGB = c.map((v) => Math.min(1, Math.max(0, v))) as RGB;
      c = mixGroup(node.kind, g, inputs.map((n) => ({ ...apply(n, g), weight: n.weight ?? 1, composite: n.composite ?? 'normal' })), node.mix ?? 1);
      return;
    }
    const { L, m } = apply(node, c);
    if (m <= 0) return;
    c = mixRgb(c, L, m);
  });
  return c;
}

/**
 * What the preview computes for one pixel from planned stages: each stage's
 * baked LUT sampled trilinearly (colour and qualifier matte), the windows
 * evaluated exactly, mixed by matte × windows × key mix, mixer inputs
 * gathered by their group. Matte blur and denoise are not modelled (they are
 * spatial). For tests and tools.
 */
export function stagesPixel(stages: readonly GradeStage[], size: number, rgb: [number, number, number], px: number, py: number, bw: number, bh: number, tLocal: number): [number, number, number] {
  let c: RGB = [...rgb];
  let g: RGB = c;
  let parts: { L: RGB; m: number; weight: number; composite: string }[] = [];
  for (const s of stages) {
    const cc: RGB = c.map((v) => Math.min(1, Math.max(0, v))) as RGB;
    if (s.group?.first) {
      g = cc;
      parts = [];
    }
    const inp = s.group ? g : cc;
    const look = sampleRgba(bakedCache(s, size), size, inp);
    let m = 1;
    if (s.keyed) {
      m = s.mix * (s.qualifier ? look[3] : 1);
      if (s.windows.length) m *= windowsMask(s.windows, tLocal, px, py, bw, bh);
    } else if (s.group) m = s.mix;
    const L: RGB = [look[0], look[1], look[2]];
    if (!s.group) {
      c = mixRgb(cc, L, m);
      continue;
    }
    parts.push({ L, m, weight: s.group.weight, composite: s.group.composite });
    if (s.group.last) c = mixGroup(s.group.kind, g, parts, s.group.mix);
  }
  return c;
}

const baked = new Map<string, Float32Array>();
function bakedCache(s: GradeStage, size: number): Float32Array {
  const key = `${size}:${s.key}:${matteKey(s.qualifier)}`;
  let hit = baked.get(key);
  if (!hit) {
    if (baked.size > 64) baked.clear();
    hit = bakeStage(s, size);
    baked.set(key, hit);
  }
  return hit;
}

/** Trilinear lookup in an RGBA LUT (0..1 domain, red fastest). */
export function sampleRgba(lut: Float32Array, size: number, rgb: readonly number[]): [number, number, number, number] {
  const n = size - 1;
  const p = [0, 1, 2].map((k) => Math.min(1, Math.max(0, rgb[k])) * n);
  const i0 = p.map((v) => Math.min(n - 1, Math.floor(v)));
  const f = p.map((v, k) => v - i0[k]);
  const out: [number, number, number, number] = [0, 0, 0, 0];
  for (let corner = 0; corner < 8; corner++) {
    const dr = corner & 1;
    const dg = (corner >> 1) & 1;
    const db = (corner >> 2) & 1;
    const w = (dr ? f[0] : 1 - f[0]) * (dg ? f[1] : 1 - f[1]) * (db ? f[2] : 1 - f[2]);
    if (!w) continue;
    const idx = ((i0[2] + db) * size * size + (i0[1] + dg) * size + (i0[0] + dr)) * 4;
    for (let c = 0; c < 4; c++) out[c] += w * lut[idx + c];
  }
  return out;
}
